import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity as ActivityIcon,
  FileText,
  KeyRound,
  Link as LinkIcon,
  Mail,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  ShieldOff,
  Shield,
} from 'lucide-react';
import { api, apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate, timeAgo } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { RoleBadge, formatRoleLabel } from '../components/system/RoleBadge';
import { UserStatusBadge } from '../components/system/UserStatusBadge';
import { ActivationLinkCard } from '../components/system/ActivationLinkCard';
import { UserDetailDrawer } from '../components/system/UserDetailDrawer';
import type { PortalUserLink, UserDetail } from '../components/system/types';

export const Route = createFileRoute('/_authed/system/users/$userId')({
  component: UserDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/**
 * Local detail extension. The backend agent ships PR 5 with the existing
 * GET /api/users/:id payload (no schema additions for this PR); the
 * `failedLoginAttempts` and `lockedUntil` fields are columns on the
 * `users` table that may be surfaced now or in a follow-up — keep them
 * optional so the route is forward-compatible with either rollout.
 */
interface UserDetailExtended extends UserDetail {
  failedLoginAttempts?: number | null;
  lockedUntil?: string | null;
}

interface SessionRow {
  id: string;
  userId: string;
  roleSnapshot: string;
  mfaLevel: 'none' | 'totp' | 'passkey';
  ipHash: string | null;
  uaHash: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  revokedAt: string | null;
  /** Marked by the backend when the session is the caller's current one. */
  isCurrent?: boolean;
}

interface SessionsResponse {
  items: SessionRow[];
}

type Tab = 'overview' | 'linked' | 'sessions' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof FileText }> = [
  { value: 'overview', label: 'Overview', icon: FileText },
  { value: 'linked', label: 'Linked profile', icon: LinkIcon },
  { value: 'sessions', label: 'Sessions', icon: ShieldOff },
  { value: 'activity', label: 'Activity', icon: ActivityIcon },
];

const READ_ROLES = ['super_admin', 'academic_admin', 'support_viewer'] as const;
const IDENTITY_EDIT_ROLES = ['super_admin', 'academic_admin'] as const;

const LINKED_PROFILE_TYPE_LABEL: Record<PortalUserLink['type'], string> = {
  teacher: 'Teacher',
  manager: 'Nalim',
  student: 'Student',
  household: 'Parent in a family',
};

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
};

const isSessionActive = (row: SessionRow): boolean => {
  if (row.revokedAt) return false;
  const expiresAt = new Date(row.expiresAt).getTime();
  if (Number.isNaN(expiresAt)) return false;
  return expiresAt > Date.now();
};

const formatMfa = (level: SessionRow['mfaLevel']): string => {
  if (level === 'none') return 'Password only';
  if (level === 'totp') return 'TOTP';
  return 'Passkey';
};

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function UserDetailPage(): JSX.Element {
  const { userId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const callerRole = useAuthStore((s) => s.user?.primaryRole ?? null);
  const callerId = useAuthStore((s) => s.user?.id ?? null);

  const canView = callerRole
    ? (READ_ROLES as readonly string[]).includes(callerRole)
    : false;
  const canEditIdentity = callerRole
    ? (IDENTITY_EDIT_ROLES as readonly string[]).includes(callerRole)
    : false;
  const canManageStatus = callerRole === 'super_admin';
  const canRevokeSession = callerRole === 'super_admin';
  const isSelf = callerId === userId;

  const [tab, setTab] = useState<Tab>('overview');
  const [drawerUserId, setDrawerUserId] = useState<string | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [reinviteUrl, setReinviteUrl] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['user', 'detail', userId] as const,
    queryFn: ({ signal }) =>
      apiRequest<UserDetailExtended>(
        `/api/users/${encodeURIComponent(userId)}`,
        { signal },
      ),
    enabled: canView,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Loaded alongside the detail query (not blocking on it) so that the
  // KPI "Active sessions" tile shows a real count without waiting for the
  // operator to open the Sessions tab. Cheap to fetch — single SELECT on
  // sessions filtered by user. Kept as its own query key so the Sessions
  // tab and the KPI share the same cache entry.
  const sessionsQuery = useQuery({
    queryKey: ['user', 'sessions', userId] as const,
    queryFn: ({ signal }) =>
      apiRequest<SessionsResponse>(
        `/api/users/${encodeURIComponent(userId)}/sessions`,
        { signal },
      ),
    enabled: canView,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const user = detailQuery.data;

  const activeSessionCount = useMemo(() => {
    const rows = sessionsQuery.data?.items ?? [];
    return rows.filter(isSessionActive).length;
  }, [sessionsQuery.data]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['user', 'detail', userId] });
    void queryClient.invalidateQueries({ queryKey: ['user', 'sessions', userId] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  // ---- Header action mutations -----------------------------------------

  const reinviteMutation = useMutation({
    mutationFn: () => api.users.reinvite(userId),
  });
  const forceResetMutation = useMutation({
    mutationFn: () => api.users.forceReset(userId),
  });
  const deactivateMutation = useMutation({
    mutationFn: () => api.users.deactivate(userId),
  });
  const reactivateMutation = useMutation({
    mutationFn: () => api.users.reactivate(userId),
  });

  const handleError = (err: unknown, fallback: string): void => {
    toast(err instanceof ApiError ? err.message : fallback, { variant: 'error' });
  };

  const handleResend = async (): Promise<void> => {
    try {
      const res = await reinviteMutation.mutateAsync();
      setReinviteUrl(res.activationUrl);
      if (user) {
        toast(`Fresh activation link generated for ${user.email}.`, {
          variant: 'success',
        });
      }
      invalidate();
    } catch (err) {
      handleError(err, 'Could not resend invitation.');
    }
  };

  const handleForceReset = async (): Promise<void> => {
    if (!user) return;
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Force password reset?',
      description: `${user.displayName} will be signed out and emailed a reset link. They must set a new password to sign back in.`,
      confirmLabel: 'Force reset',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await forceResetMutation.mutateAsync();
      toast('Password reset link emailed.', { variant: 'success' });
      invalidate();
    } catch (err) {
      handleError(err, 'Could not force reset.');
    }
  };

  const handleDeactivate = async (): Promise<void> => {
    if (!user) return;
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Deactivate user?',
      description: `${user.displayName} will lose portal access immediately and all active sessions will end.`,
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deactivateMutation.mutateAsync();
      toast('User deactivated.', { variant: 'success' });
      invalidate();
    } catch (err) {
      handleError(err, 'Could not deactivate user.');
    }
  };

  const handleReactivate = async (): Promise<void> => {
    setOverflowOpen(false);
    try {
      await reactivateMutation.mutateAsync();
      toast('User reactivated.', { variant: 'success' });
      invalidate();
    } catch (err) {
      handleError(err, 'Could not reactivate user.');
    }
  };

  // ---- KPI strip --------------------------------------------------------

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!user) return [];
    const linkedTypeName = user.linked
      ? LINKED_PROFILE_TYPE_LABEL[user.linked.type]
      : 'Not linked';
    return [
      {
        label: 'Status',
        value: <UserStatusBadge status={user.status} />,
      },
      {
        label: 'Last sign-in',
        value: user.lastLoginAt ? timeAgo(user.lastLoginAt) : 'Never',
      },
      {
        label: 'Linked profile',
        value: linkedTypeName,
        sub: user.linked ? user.linked.label : 'No teacher / student / parent record',
        onClick: () => setTab('linked'),
      },
      {
        label: 'Active sessions',
        value: sessionsQuery.isLoading ? (
          <Skeleton className="h-7 w-10" />
        ) : (
          activeSessionCount
        ),
        onClick: () => setTab('sessions'),
      },
    ];
  }, [user, sessionsQuery.isLoading, activeSessionCount]);

  if (!canView) {
    return (
      <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
        <EmptyState
          title="No access"
          description="You do not have permission to view this user."
        />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="User"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !user ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={user.displayName}
              sublabel={
                <>
                  <span className="break-all">{user.email}</span>
                  <span aria-hidden="true">·</span>
                  <span>Joined {formatDate(user.createdAt)}</span>
                </>
              }
              backTo="/system/users"
              leading={
                <span
                  aria-hidden="true"
                  className="grid h-12 w-12 place-items-center rounded-full bg-gold-100 text-base font-semibold text-gold-900"
                >
                  {initialsOf(user.displayName)}
                </span>
              }
              statusChip={
                <span className="inline-flex flex-wrap items-center gap-2">
                  <RoleBadge role={user.primaryRole} />
                  <UserStatusBadge status={user.status} />
                </span>
              }
              actions={
                <HeaderActions
                  user={user}
                  isSelf={isSelf}
                  canEditIdentity={canEditIdentity}
                  canManageStatus={canManageStatus}
                  overflowOpen={overflowOpen}
                  setOverflowOpen={setOverflowOpen}
                  reinvitePending={reinviteMutation.isPending}
                  forceResetPending={forceResetMutation.isPending}
                  deactivatePending={deactivateMutation.isPending}
                  reactivatePending={reactivateMutation.isPending}
                  onEdit={() => setDrawerUserId(user.id)}
                  onResend={() => {
                    void handleResend();
                  }}
                  onForceReset={() => {
                    void handleForceReset();
                  }}
                  onDeactivate={() => {
                    void handleDeactivate();
                  }}
                  onReactivate={() => {
                    void handleReactivate();
                  }}
                />
              }
            />

            {reinviteUrl ? (
              <ActivationLinkCard
                url={reinviteUrl}
                recipient={user.email}
                context="reinvite"
                onDismiss={() => setReinviteUrl(null)}
              />
            ) : null}

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="User sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? <OverviewTab user={user} /> : null}
            {tab === 'linked' ? (
              <LinkedProfileTab
                user={user}
                canEditIdentity={canEditIdentity}
                onLink={() => setDrawerUserId(user.id)}
              />
            ) : null}
            {tab === 'sessions' ? (
              <SessionsTab
                userId={user.id}
                userName={user.displayName}
                canRevoke={canRevokeSession}
                sessions={sessionsQuery.data?.items ?? null}
                isLoading={sessionsQuery.isLoading && !sessionsQuery.data}
                error={sessionsQuery.error}
                onRetry={() => {
                  void sessionsQuery.refetch();
                }}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="user"
                targetId={user.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <UserDetailDrawer
        userId={drawerUserId}
        onClose={() => setDrawerUserId(null)}
        onChange={() => {
          invalidate();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header action cluster
// ---------------------------------------------------------------------------

interface HeaderActionsProps {
  user: UserDetailExtended;
  isSelf: boolean;
  canEditIdentity: boolean;
  canManageStatus: boolean;
  overflowOpen: boolean;
  setOverflowOpen: (next: boolean) => void;
  reinvitePending: boolean;
  forceResetPending: boolean;
  deactivatePending: boolean;
  reactivatePending: boolean;
  onEdit: () => void;
  onResend: () => void;
  onForceReset: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}

function HeaderActions({
  user,
  isSelf,
  canEditIdentity,
  canManageStatus,
  overflowOpen,
  setOverflowOpen,
  reinvitePending,
  forceResetPending,
  deactivatePending,
  reactivatePending,
  onEdit,
  onResend,
  onForceReset,
  onDeactivate,
  onReactivate,
}: HeaderActionsProps): JSX.Element | null {
  // Stable-but-flat "more" items list so we can hide the entire menu
  // when nothing inside applies (e.g. support_viewer, or super_admin
  // looking at their own row).
  const items: ReactNode[] = [];

  if (canManageStatus && !isSelf && (user.status === 'active' || user.status === 'pending')) {
    items.push(
      <button
        key="force-reset"
        role="menuitem"
        type="button"
        onClick={onForceReset}
        disabled={forceResetPending}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none disabled:opacity-50"
      >
        <KeyRound size={14} aria-hidden="true" />
        Force password reset
      </button>,
    );
  }

  if (canManageStatus && !isSelf && user.status === 'active') {
    items.push(
      <button
        key="deactivate"
        role="menuitem"
        type="button"
        onClick={onDeactivate}
        disabled={deactivatePending}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 focus:outline-none disabled:opacity-50"
      >
        <PowerOff size={14} aria-hidden="true" />
        Deactivate
      </button>,
    );
  }

  if (canManageStatus && user.status === 'inactive') {
    items.push(
      <button
        key="reactivate"
        role="menuitem"
        type="button"
        onClick={onReactivate}
        disabled={reactivatePending}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none disabled:opacity-50"
      >
        <Power size={14} aria-hidden="true" />
        Reactivate
      </button>,
    );
  }

  const showEdit = canEditIdentity;
  const showResend = canEditIdentity && user.status === 'pending';
  const showOverflow = items.length > 0;

  if (!showEdit && !showResend && !showOverflow) {
    return null;
  }

  return (
    <>
      {showEdit ? (
        <Button
          variant="secondary"
          size="sm"
          icon={<Pencil size={14} aria-hidden="true" />}
          onClick={onEdit}
        >
          Edit
        </Button>
      ) : null}
      {showResend ? (
        <Button
          size="sm"
          icon={<Mail size={14} aria-hidden="true" />}
          loading={reinvitePending}
          onClick={onResend}
        >
          Resend invitation
        </Button>
      ) : null}
      {showOverflow ? (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            icon={<MoreHorizontal size={14} aria-hidden="true" />}
            onClick={() => setOverflowOpen(!overflowOpen)}
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
          >
            More
          </Button>
          {overflowOpen ? (
            <div
              role="menu"
              className="absolute right-0 z-10 mt-1 min-w-[200px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
            >
              {items}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ user }: { user: UserDetailExtended }): JSX.Element {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Identity
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow label="Email" value={<span className="break-all">{user.email}</span>} />
          <DetailRow label="Primary role" value={formatRoleLabel(user.primaryRole)} />
          <DetailRow label="Status" value={<UserStatusBadge status={user.status} />} />
          <DetailRow label="Joined" value={formatDate(user.createdAt)} />
        </dl>
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Security
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow
            label="Last sign-in"
            value={user.lastLoginAt ? timeAgo(user.lastLoginAt) : 'Never'}
          />
          <DetailRow
            label="Must reset password"
            value={user.mustResetPassword ? 'Yes' : 'No'}
          />
          <DetailRow
            label="Failed login attempts"
            value={
              typeof user.failedLoginAttempts === 'number'
                ? String(user.failedLoginAttempts)
                : '—'
            }
          />
          <DetailRow
            label="Locked until"
            value={user.lockedUntil ? formatDate(user.lockedUntil, { withTime: true }) : '—'}
          />
        </dl>
      </div>
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="text-sm text-[color:var(--color-text)]">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linked profile tab
// ---------------------------------------------------------------------------

interface LinkedProfileTabProps {
  user: UserDetailExtended;
  canEditIdentity: boolean;
  onLink: () => void;
}

function LinkedProfileTab({
  user,
  canEditIdentity,
  onLink,
}: LinkedProfileTabProps): JSX.Element {
  const linked = user.linked ?? null;

  if (!linked) {
    return (
      <RelationPanel
        title="Linked profile"
        description="The teacher / student / parent record this user signs in as."
        isEmpty
        empty={{
          copy: 'This user is not linked to a profile.',
          cta: canEditIdentity ? (
            <Button
              size="sm"
              icon={<LinkIcon size={14} aria-hidden="true" />}
              onClick={onLink}
            >
              Link profile
            </Button>
          ) : undefined,
        }}
      >
        <span />
      </RelationPanel>
    );
  }

  return (
    <RelationPanel
      title="Linked profile"
      description={LINKED_PROFILE_TYPE_LABEL[linked.type]}
      action={
        canEditIdentity ? (
          <Button size="sm" variant="ghost" onClick={onLink}>
            Change
          </Button>
        ) : null
      }
    >
      <LinkedProfileCard linked={linked} />
    </RelationPanel>
  );
}

function LinkedProfileCard({ linked }: { linked: PortalUserLink }): JSX.Element {
  return (
    <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[color:var(--color-text)]">
            {linked.label}
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
            {LINKED_PROFILE_TYPE_LABEL[linked.type]}
          </p>
        </div>
        <LinkedProfileOpenLink linked={linked} />
      </div>
    </div>
  );
}

function LinkedProfileOpenLink({ linked }: { linked: PortalUserLink }): JSX.Element {
  const label = 'Open profile';
  const className = 'text-xs font-medium text-gold-700 hover:underline';

  if (linked.type === 'teacher') {
    return (
      <Link
        to="/people/teachers/$teacherId"
        params={{ teacherId: linked.id }}
        className={className}
      >
        {label}
      </Link>
    );
  }
  if (linked.type === 'manager') {
    return (
      <Link
        to="/people/managers/$managerId"
        params={{ managerId: linked.id }}
        className={className}
      >
        {label}
      </Link>
    );
  }
  if (linked.type === 'student') {
    return (
      <Link
        to="/people/students/$studentId"
        params={{ studentId: linked.id }}
        className={className}
      >
        {label}
      </Link>
    );
  }
  return (
    <Link
      to="/people/households/$householdId"
      params={{ householdId: linked.id }}
      className={className}
    >
      {label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Sessions tab
// ---------------------------------------------------------------------------

interface SessionsTabProps {
  userId: string;
  userName: string;
  canRevoke: boolean;
  sessions: SessionRow[] | null;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  onChanged: () => void;
}

function SessionsTab({
  userId,
  userName,
  canRevoke,
  sessions,
  isLoading,
  error,
  onRetry,
  onChanged,
}: SessionsTabProps): JSX.Element {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [revokeAllPending, setRevokeAllPending] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const rows = sessions ?? [];
  const activeCount = rows.filter(isSessionActive).length;

  const handleRevoke = async (row: SessionRow): Promise<void> => {
    const ok = await confirm({
      title: 'Revoke session?',
      description: 'This will sign the user out of the matching device.',
      confirmLabel: 'Revoke',
      variant: 'danger',
    });
    if (!ok) return;
    setRevokingId(row.id);
    try {
      await apiRequest<{ ok: true }>(
        `/api/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(row.id)}/revoke`,
        { method: 'POST' },
      );
      toast('Session revoked.', { variant: 'success' });
      onChanged();
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.code === 'cannot_revoke_self'
      ) {
        toast('You cannot revoke your own current session.', { variant: 'error' });
        return;
      }
      toast(err instanceof ApiError ? err.message : 'Could not revoke session.', {
        variant: 'error',
      });
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAll = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Revoke all sessions?',
      description: `Every active session for ${userName} will be ended. They will need to sign in again.`,
      confirmLabel: 'Revoke all',
      variant: 'danger',
    });
    if (!ok) return;
    setRevokeAllPending(true);
    try {
      const res = await apiRequest<{ ok: true; revoked: number }>(
        `/api/users/${encodeURIComponent(userId)}/sessions/revoke-all`,
        { method: 'POST' },
      );
      toast(
        res.revoked === 1
          ? '1 session revoked.'
          : `${res.revoked} sessions revoked.`,
        { variant: 'success' },
      );
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not revoke sessions.', {
        variant: 'error',
      });
    } finally {
      setRevokeAllPending(false);
    }
  };

  const errorState =
    error && !sessions
      ? {
          message:
            error instanceof ApiError ? error.message : 'Could not load sessions.',
          onRetry,
        }
      : null;

  return (
    <RelationPanel
      title="Active sessions"
      count={activeCount}
      description="Live sign-ins for this user. Revoking a session signs that device out."
      loading={isLoading}
      error={errorState}
      isEmpty={!isLoading && rows.length === 0}
      empty={{ copy: 'No active sessions.' }}
      action={
        canRevoke && activeCount > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<ShieldOff size={14} aria-hidden="true" />}
            loading={revokeAllPending}
            onClick={() => {
              void handleRevokeAll();
            }}
          >
            Revoke all
          </Button>
        ) : null
      }
    >
      <div className="overflow-x-auto rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-3 py-2">Last seen</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Role at sign-in</th>
              <th className="px-3 py-2">MFA</th>
              <th className="px-3 py-2">State</th>
              {canRevoke ? <th className="px-3 py-2 text-right">Revoke</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const active = isSessionActive(row);
              return (
                <tr
                  key={row.id}
                  className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
                >
                  <td
                    className="px-3 py-2 align-middle text-xs text-[color:var(--color-text)]"
                    title={row.lastSeenAt}
                  >
                    {timeAgo(row.lastSeenAt)}
                  </td>
                  <td
                    className="px-3 py-2 align-middle text-xs text-[color:var(--color-text-muted)]"
                    title={row.createdAt}
                  >
                    {timeAgo(row.createdAt)}
                  </td>
                  <td
                    className="px-3 py-2 align-middle text-xs text-[color:var(--color-text-muted)]"
                    title={row.expiresAt}
                  >
                    {timeAgo(row.expiresAt)}
                  </td>
                  <td className="px-3 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                    {row.roleSnapshot.replace(/_/g, ' ')}
                  </td>
                  <td className="px-3 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    <span className="inline-flex items-center gap-1">
                      <Shield size={12} aria-hidden="true" />
                      {formatMfa(row.mfaLevel)}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <SessionStateChip active={active} isCurrent={row.isCurrent === true} />
                  </td>
                  {canRevoke ? (
                    <td className="px-3 py-2 align-middle text-right">
                      {active ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={revokingId === row.id}
                          onClick={() => {
                            void handleRevoke(row);
                          }}
                          aria-label="Revoke session"
                          icon={<ShieldOff size={14} aria-hidden="true" />}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <span className="text-xs text-[color:var(--color-text-muted)]">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

function SessionStateChip({
  active,
  isCurrent,
}: {
  active: boolean;
  isCurrent: boolean;
}): JSX.Element {
  if (isCurrent && active) {
    return (
      <span className="inline-flex items-center rounded-full bg-gold-50 px-2 py-0.5 text-[11px] font-semibold text-gold-700 dark:bg-gold-700/20">
        Current
      </span>
    );
  }
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-[color:var(--color-success)]/15 px-2 py-0.5 text-[11px] font-semibold text-[color:var(--color-success)]">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--color-text-muted)]">
      Revoked
    </span>
  );
}
